// HCEN Patient Website — Help & Support
// Mirrors lib/screens/support/{help_support,new_ticket,ticket_detail}_screen.dart.
// Three view-modes within one route, gated by local state:
//   1. list   — list of my tickets + "Raise a Ticket" CTA
//   2. new    — create a ticket
//   3. detail — view a ticket thread, reply, reopen, rate

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MdArrowBack, MdSupport, MdAdd, MdSend, MdStar, MdStarBorder,
  MdReplay, MdRefresh,
} from 'react-icons/md';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';

const CATEGORIES = [
  { id: 'booking',   label: 'Booking issue',   hint: 'Wrong slot, missing confirmation, etc.' },
  { id: 'payment',   label: 'Payment / refund', hint: 'Charge failed, refund pending, etc.' },
  { id: 'doctor',    label: 'Doctor / consultation', hint: 'No-show, rude staff, mis-diagnosis' },
  { id: 'emergency', label: 'Emergency / SOS',  hint: 'Ambulance dispatch, ER help' },
  { id: 'app',       label: 'App / website bug', hint: 'Crashes, slow page, broken link' },
  { id: 'other',     label: 'Other',             hint: 'Anything else' },
];

const STATUS_STYLE = {
  open:        { bg: '#fff3cd', fg: '#856404', label: 'Open' },
  in_progress: { bg: '#cce5ff', fg: '#004085', label: 'In Progress' },
  resolved:    { bg: '#d4edda', fg: '#155724', label: 'Resolved' },
  closed:      { bg: '#e2e3e5', fg: '#383d41', label: 'Closed' },
};

export default function SupportPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [view, setView]       = useState('list');     // 'list' | 'new' | 'detail'
  const [active, setActive]   = useState(null);       // active ticketId for 'detail'

  if (!user) {
    return (
      <div className="page empty fade-in">
        <div className="empty-icon">🎧</div>
        <div className="empty-title">Login to access Support</div>
        <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => navigate('/login')}>
          Login / Sign Up
        </button>
      </div>
    );
  }

  if (view === 'new') {
    return <NewTicket user={user}
      onBack={() => setView('list')}
      onCreated={(t) => { setActive(t.ticketId); setView('detail'); }} />;
  }

  if (view === 'detail' && active) {
    return <TicketDetail user={user} ticketId={active}
      onBack={() => { setActive(null); setView('list'); }} />;
  }

  return <TicketList user={user}
    onNew={() => setView('new')}
    onOpen={(id) => { setActive(id); setView('detail'); }} />;
}

// ─── List ─────────────────────────────────────────────────────────
function TicketList({ user, onNew, onOpen }) {
  const [items, setItems]   = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await api.myTickets({ phone: user.phoneDigits || user.phone, userId: user.patientId });
    setItems(r.data?.tickets || []);
    setLoading(false);
  }, [user]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="page fade-in" style={{ maxWidth: 640 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
        <MdSupport size={26} style={{ color: 'var(--red, #d32f2f)' }} />
        <div style={{ fontSize: 22, fontWeight: 800, flex: 1 }}>Help &amp; Support</div>
        <button className="btn btn-ghost btn-sm" onClick={load} title="Refresh">
          <MdRefresh size={18} />
        </button>
      </div>

      <button className="btn btn-primary btn-full btn-lg" onClick={onNew}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 16 }}>
        <MdAdd size={18} /> Raise a Ticket
      </button>


      <div style={{ fontWeight: 700, marginBottom: 10, marginTop: 4 }}>My tickets</div>

      {loading ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
      ) : items.length === 0 ? (
        <div className="glass glass-p" style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 32 }}>
          No tickets yet. Tap “Raise a Ticket” above if you need help.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {items.map(t => {
            const s = STATUS_STYLE[t.status] || STATUS_STYLE.open;
            return (
              <button key={t.ticketId} onClick={() => onOpen(t.ticketId)}
                style={{
                  textAlign: 'left', padding: 14, background: 'var(--bg-card)',
                  border: '1px solid var(--border)', borderRadius: 12, cursor: 'pointer',
                  color: 'var(--text)', display: 'flex', flexDirection: 'column', gap: 6,
                }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 8,
                    background: s.bg, color: s.fg,
                  }}>{s.label}</span>
                  <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t.category}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)' }}>
                    {timeAgo(t.updatedAt || t.createdAt)}
                  </span>
                </div>
                <div style={{ fontWeight: 700 }}>{t.subject}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>#{t.ticketId}</div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── New Ticket ───────────────────────────────────────────────────
function NewTicket({ user, onBack, onCreated }) {
  const [subject, setSubject]   = useState('');
  const [category, setCategory] = useState('booking');
  const [desc, setDesc]         = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]       = useState(null);
  const { showToast } = useAuth();

  const submit = async () => {
    setError(null);
    if (subject.trim().length < 3)  { setError('Subject is required (min 3 chars)'); return; }
    if (desc.trim().length    < 5)  { setError('Please describe the issue (min 5 chars)'); return; }
    setSubmitting(true);
    const r = await api.createTicket({
      subject:     subject.trim(),
      description: desc.trim(),
      category,
      userPhone:   user.phoneDigits || String(user.phone || '').replace(/\D/g, '').slice(-10),
      userId:      user.patientId,
      userName:    user.name || 'Patient',
    });
    setSubmitting(false);
    if (r.error) { setError(r.error); return; }
    showToast('Ticket raised — we\'ll get back to you soon ✅', 'success');
    onCreated(r.data?.ticket || {});
  };

  return (
    <div className="page fade-in" style={{ maxWidth: 560 }}>
      <button onClick={onBack} className="btn btn-ghost btn-sm" style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
        <MdArrowBack size={16} /> Back
      </button>
      <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 16 }}>Raise a Ticket</div>

      <div className="glass glass-p" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Category */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 8, letterSpacing: 0.5 }}>CATEGORY</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8 }}>
            {CATEGORIES.map(c => {
              const sel = category === c.id;
              return (
                <button key={c.id} type="button" onClick={() => setCategory(c.id)} style={{
                  padding: '10px 12px', borderRadius: 10, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                  border: '1px solid', textAlign: 'left',
                  borderColor: sel ? 'var(--red, #d32f2f)' : 'var(--border)',
                  background: sel ? 'rgba(211,47,47,0.08)' : 'var(--bg-card)',
                  color: sel ? 'var(--red, #d32f2f)' : 'var(--text)',
                }}>
                  <div>{c.label}</div>
                  <div style={{ fontSize: 10, fontWeight: 500, color: 'var(--text-muted)', marginTop: 2 }}>{c.hint}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Subject */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 6, letterSpacing: 0.5 }}>SUBJECT</div>
          <input className="input" value={subject} onChange={e => setSubject(e.target.value)}
            placeholder="Short summary of the issue" maxLength={120}
            style={{ width: '100%', padding: '10px 12px', fontSize: 14 }} />
        </div>

        {/* Description */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 6, letterSpacing: 0.5 }}>DESCRIPTION</div>
          <textarea value={desc} onChange={e => setDesc(e.target.value)} rows={6} maxLength={2000}
            placeholder="Tell us what happened, when, and what you expected. Include booking IDs if applicable."
            className="input" style={{ width: '100%', padding: 12, fontSize: 14, resize: 'vertical', fontFamily: 'inherit' }} />
          <div style={{ textAlign: 'right', fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
            {desc.length}/2000
          </div>
        </div>

        {error && (
          <div style={{ padding: 10, borderRadius: 8, background: 'rgba(239,68,68,0.1)', color: '#dc2626', fontSize: 13 }}>
            {error}
          </div>
        )}

        <button className="btn btn-primary btn-lg" disabled={submitting} onClick={submit}>
          {submitting ? 'Submitting…' : 'Submit Ticket'}
        </button>
      </div>
    </div>
  );
}

// ─── Detail ───────────────────────────────────────────────────────
function TicketDetail({ user, ticketId, onBack }) {
  const [t, setT]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply]     = useState('');
  const [busy, setBusy]       = useState(false);
  const [showRate, setShowRate] = useState(false);
  const [rating, setRating]   = useState(0);
  const [rateComment, setRateComment] = useState('');
  const { showToast } = useAuth();

  const load = useCallback(async () => {
    setLoading(true);
    const r = await api.getTicket(ticketId);
    setT(r.data?.ticket || null);
    setLoading(false);
  }, [ticketId]);

  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    const r = await api.replyTicket(ticketId, reply.trim());
    setBusy(false);
    if (r.error) { showToast(r.error, 'error'); return; }
    setReply('');
    load();
  };

  const reopen = async () => {
    setBusy(true);
    const r = await api.reopenTicket(ticketId);
    setBusy(false);
    if (r.error) { showToast(r.error, 'error'); return; }
    showToast('Ticket reopened', 'info');
    load();
  };

  const submitRating = async () => {
    if (!rating) { showToast('Pick a star rating', 'error'); return; }
    setBusy(true);
    const r = await api.rateTicket(ticketId, rating, rateComment.trim());
    setBusy(false);
    if (r.error) { showToast(r.error, 'error'); return; }
    showToast('Thanks for your feedback ⭐', 'success');
    setShowRate(false);
    load();
  };

  if (loading) return (
    <div className="page fade-in" style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)' }}>
      Loading ticket…
    </div>
  );

  if (!t) return (
    <div className="page fade-in" style={{ padding: 24 }}>
      <button onClick={onBack} className="btn btn-ghost btn-sm" style={{ marginBottom: 14 }}>
        <MdArrowBack size={16} /> Back
      </button>
      <div className="glass glass-p">Ticket not found.</div>
    </div>
  );

  const s = STATUS_STYLE[t.status] || STATUS_STYLE.open;
  const isResolved = t.status === 'resolved';
  const isClosed   = t.status === 'closed';
  const canReply   = !isClosed;

  return (
    <div className="page fade-in" style={{ maxWidth: 640 }}>
      <button onClick={onBack} className="btn btn-ghost btn-sm" style={{ marginBottom: 14, display: 'flex', alignItems: 'center', gap: 6 }}>
        <MdArrowBack size={16} /> Back
      </button>

      {/* Header */}
      <div className="glass glass-p" style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <span style={{
            fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 8,
            background: s.bg, color: s.fg,
          }}>{s.label}</span>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{t.category}</span>
          <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)' }}>#{t.ticketId}</span>
        </div>
        <div style={{ fontSize: 17, fontWeight: 800 }}>{t.subject}</div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
          Raised {timeAgo(t.createdAt)} · Last updated {timeAgo(t.updatedAt)}
        </div>
        {t.rating && (
          <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            Your rating:
            {[1,2,3,4,5].map(i => i <= t.rating
              ? <MdStar       key={i} size={16} style={{ color: '#fbbf24' }} />
              : <MdStarBorder key={i} size={16} style={{ color: 'var(--text-muted)' }} />)}
          </div>
        )}
      </div>

      {/* Thread */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
        {(t.messages || []).map((m, i) => {
          const mine = m.sender === 'user';
          return (
            <div key={i} style={{
              alignSelf: mine ? 'flex-end' : 'flex-start',
              maxWidth: '85%',
              background: mine ? 'rgba(211,47,47,0.10)' : 'var(--bg-card)',
              border: '1px solid', borderColor: mine ? 'rgba(211,47,47,0.25)' : 'var(--border)',
              padding: '10px 12px', borderRadius: 12,
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 4 }}>
                {mine ? 'You' : (m.senderName || 'HCEN Support')} · {timeAgo(m.at)}
              </div>
              <div style={{ fontSize: 14, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{m.text}</div>
            </div>
          );
        })}
      </div>

      {/* Actions */}
      {canReply && (
        <div className="glass glass-p" style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 6, letterSpacing: 0.5 }}>
            ADD A MESSAGE
          </div>
          <textarea value={reply} onChange={e => setReply(e.target.value)} rows={3} maxLength={2000}
            className="input" placeholder="Type your reply…"
            style={{ width: '100%', padding: 10, fontSize: 14, resize: 'vertical', fontFamily: 'inherit' }} />
          <div style={{ display: 'flex', gap: 8, marginTop: 10, justifyContent: 'flex-end' }}>
            <button className="btn btn-primary btn-sm" disabled={busy || !reply.trim()} onClick={send}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <MdSend size={14} /> Send
            </button>
          </div>
        </div>
      )}

      {isResolved && (
        <div className="glass glass-p" style={{ marginBottom: 14 }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>This issue is resolved 🎉</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {!t.rating && (
              <button className="btn btn-primary btn-sm" onClick={() => setShowRate(true)}
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <MdStar size={14} /> Rate this resolution
              </button>
            )}
            <button className="btn btn-secondary btn-sm" onClick={reopen} disabled={busy}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <MdReplay size={14} /> Reopen
            </button>
          </div>
        </div>
      )}

      {showRate && (
        <div className="glass glass-p" style={{ marginBottom: 14, borderColor: 'rgba(245,158,11,0.4)' }}>
          <div style={{ fontWeight: 700, marginBottom: 10 }}>How did we do?</div>
          <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
            {[1,2,3,4,5].map(i => (
              <button key={i} onClick={() => setRating(i)}
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}>
                {i <= rating
                  ? <MdStar       size={28} style={{ color: '#fbbf24' }} />
                  : <MdStarBorder size={28} style={{ color: 'var(--text-muted)' }} />}
              </button>
            ))}
          </div>
          <textarea value={rateComment} onChange={e => setRateComment(e.target.value)}
            rows={2} maxLength={400} placeholder="Optional comment…"
            className="input" style={{ width: '100%', padding: 8, fontSize: 13, marginBottom: 10, resize: 'vertical', fontFamily: 'inherit' }} />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn btn-secondary btn-sm" onClick={() => setShowRate(false)}>Cancel</button>
            <button className="btn btn-primary btn-sm" disabled={busy || !rating} onClick={submitRating}>Submit</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────
function timeAgo(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return '';
  const s = Math.floor(ms / 1000);
  if (s < 60)       return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60)       return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)       return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7)        return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}
